import { Handle, Position } from "@xyflow/react";
import { Clock, Cloud, Edit3, GitBranch, MoreVertical, Network, Settings, Split, Terminal, Timer, Unplug, Upload } from "lucide-react";
import { ACTIVITY_TYPES } from "../helpers";
import { Dropdown } from "Common/Components/Dropdown";
import { Link } from "react-router-dom";
import { useEffect, useMemo } from "react";

const CustomNode = ({ data, selected }: any) => {
  const nodeWidth = 200;
  const baseNodeHeight = 64;

  const getStepIcon = (type: string) => {
    switch (type) {
      case ACTIVITY_TYPES.DELAY:
        return <Timer className="h-4 w-4" />
      case ACTIVITY_TYPES.CRON:
        return <Clock className="h-4 w-4" />
      case ACTIVITY_TYPES.TIMER:
        return <Timer className="h-4 w-4" />
      case ACTIVITY_TYPES.WRITE_LINE:
        return <Edit3 className="h-4 w-4" />
      case ACTIVITY_TYPES.READ_LINE:
        return <Terminal className="h-4 w-4" />
      case ACTIVITY_TYPES.IF:
        return <GitBranch className="h-4 w-4" />
      case ACTIVITY_TYPES.SWITCH:
        return <Split className="h-4 w-4" />
      case ACTIVITY_TYPES.FLOW_SEND_HTTP_REQUEST:
        return <Network className="h-4 w-4" />
      case ACTIVITY_TYPES.WRITE_HTTP_RESPONSE:
        return <Upload className="h-4 w-4" />
      case ACTIVITY_TYPES.HTTP_ENDPOINT:
        return <Cloud className="h-4 w-4" />
      case ACTIVITY_TYPES.DISPATCH_WORKFLOW:
      case ACTIVITY_TYPES.SET_OUTPUT:
        return <Unplug className="h-4 w-4" />
      default:
        return <Settings className="h-4 w-4" />
    }
  }

  const getStepColor = (type: string) => {
    switch (type) {
      case ACTIVITY_TYPES.SEND_EMAIL_MARKETING:
      case ACTIVITY_TYPES.SEND_MESSAGE_REMINDER:
        return 'bg-blue-400 border-blue-400'
      case ACTIVITY_TYPES.DELAY:
      case ACTIVITY_TYPES.CRON:
      case ACTIVITY_TYPES.TIMER:
        return 'bg-amber-400 border-amber-400'
      case ACTIVITY_TYPES.WRITE_LINE:
      case ACTIVITY_TYPES.READ_LINE:
        return 'bg-sky-400 border-sky-400'
      case ACTIVITY_TYPES.IF:
      case ACTIVITY_TYPES.SWITCH:
        return 'bg-purple-400 border-purple-400'
      case ACTIVITY_TYPES.HTTP_ENDPOINT:
      case ACTIVITY_TYPES.WRITE_HTTP_RESPONSE:
      case ACTIVITY_TYPES.FLOW_SEND_HTTP_REQUEST:
        return 'bg-gray-400 border-gray-400'
      case ACTIVITY_TYPES.DISPATCH_WORKFLOW:
      case ACTIVITY_TYPES.SET_OUTPUT:
        return 'bg-orange-400 border-orange-400'
      default:
        return 'bg-slate-400 border-slate-400'
    }
  }

  const getActivityMetadata = (activity: any) => {
    const metadata: string[] = [];
    // Return early if no parameters or empty parameters
    if (!activity.parameters || Object.keys(activity.parameters).length === 0) {
      return metadata;
    }

    // Add metadata based on activity type and parameters
    switch (activity.type) {
      default:
        const paramEntries = Object.entries(activity.parameters);
        for (const [key, value] of paramEntries) {
          if (key.toUpperCase() === 'AUTHORIZATION') continue;
          if (key.toUpperCase() === 'RESULT') continue;
          if (key.toUpperCase() === 'CONTENT') continue;
          // Skip sensitive keys
          if (value && typeof value === 'string' && value.trim().length > 0) {
            const truncated = value.length > 25 ? value.substring(0, 50) + '...' : value;
            metadata.push(`${key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}: ${truncated}`);
          }
        }
        break;
    }

    return metadata;
  }

  const handleDelete = (e: any) => {
    data.onRemove?.();
  }

  const handleConfig = (e: any) => {
    data.onEdit?.();
  }

  // Memoize the metadata calculation to avoid recalculating on every render
  const activityMetadata = useMemo(() => getActivityMetadata(data), [
    data.type,
    data.parameters,
    data.name,
    data.description,
    data.lastUpdated,
    data.key
  ]);

  // Calculate dynamic height based on metadata content
  const nodeHeight = useMemo(() => {
    return baseNodeHeight + (activityMetadata.length * 28); // Add ~28px per metadata item
  }, [activityMetadata.length]);

  useEffect(() => {
    if (data.onLoad) {
      data.onLoad();
    }
  }, [data.id, data.type, data.name, data.description, data.parameters, data.onLoad])

  return (
    <>
      <div onClick={() => { }} className={`bg-white rounded-lg shadow-lg border-2 p-4 min-w-[180px] transition-all duration-200 ${selected ? 'border-blue-400 shadow-xl scale-105 z-20' : 'border-gray-200 hover:border-gray-300 hover:shadow-xl z-10'}`}
        style={{ minWidth: nodeWidth, minHeight: nodeHeight, position: 'relative' }}>
        {/* Left handle (incoming) */}
        <Handle
          type="target"
          position={Position.Left}
          style={{ background: '#2563eb', borderRadius: '50%', width: 12, height: 12, left: -8, top: '50%', transform: 'translateY(-50%)' }}
          id="left-target"
        />
        {/* Right handle (outgoing) */}
        <Handle
          type="source"
          position={Position.Right}
          style={{ background: '#2563eb', borderRadius: '50%', width: 12, height: 12, right: -8, top: '50%', transform: 'translateY(-50%)' }}
          id="right-source"
        />
        <div className="flex items-start gap-3">
          <div className={`
            flex items-center justify-center w-6 h-6 text-white rounded-full
            ${getStepColor(data.type)}
          `}>
            {getStepIcon(data.type)}
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-gray-800 text-sm">{data.name || data.type}</h4>
            <p className="text-xs text-gray-500 mt-1 mb-4">{activityMetadata.length <= 0 ? data.description : null}</p>
            {/* Display activity metadata */}
            {activityMetadata.length > 0 && (
              <div className="mt-1 space-y-1">
                {activityMetadata.map((meta, index) => (
                  <p key={index} className="text-xs text-blue-600 bg-blue-50 px-1 py-1 rounded">
                    {meta}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="absolute -top-1 right-[-15px] transition-opacity duration-200">
          <Dropdown className="relative transition-all duration-200 ease-linear  dropdown shrink-0 group-[.right]/item:order-1">
            <Dropdown.Trigger type="button" className="dropdown-toggle" id="dropdownMenuButton" data-bs-toggle="dropdown"><MoreVertical className="inline-block size-4 ml-1" /></Dropdown.Trigger>

            <Dropdown.Content placement="workflow" className="absolute z-50 py-2 mt-1 ltr:text-left rtl:text-right list-none bg-white rounded-md shadow-md dropdown-menu min-w-[10rem] dark:bg-zink-600" aria-labelledby="dropdownMenuButton">
              <li>
                <Link className="block px-4 py-1.5 text-base transition-all duration-200 ease-linear text-slate-600 dropdown-item hover:bg-slate-100 hover:text-slate-500 focus:bg-slate-100 focus:text-slate-500 dark:text-zink-100 dark:hover:bg-zink-500 dark:hover:text-zink-200 dark:focus:bg-zink-500 dark:focus:text-zink-200" to="#!" onClick={handleConfig}>Settings</Link>
              </li>
              <li>
                <Link className="block px-4 py-1.5 text-base transition-all duration-200 ease-linear text-slate-600 dropdown-item hover:bg-slate-100 hover:text-slate-500 focus:bg-slate-100 focus:text-slate-500 dark:text-zink-100 dark:hover:bg-zink-500 dark:hover:text-zink-200 dark:focus:bg-zink-500 dark:focus:text-zink-200" to="#!" onClick={handleDelete}>Delete</Link>
              </li>
            </Dropdown.Content>
          </Dropdown>
        </div>
      </div>
    </>
  );
}

export default CustomNode;